import { EDITOR_VIEW_NAME } from "../../core/constants";
import { getViewData, setViewData } from "../../core/views";
import { doRequest } from "../http";
import { migrateTabUserSources } from "../migrate";
import { decompressBase64 } from "../zipping";
import { getCloudData, getCloudServerPath, getCloudUser, setCloudData } from "./cloud";
import { isV2CloudData, loadV2CloudZip } from "../filesystem";

export const wpJsonState = {
  loggedIn: false,
  nonce: "",
  dataKey: null,
  data: null,
  project: null
}

export const USER_REGISTERED_EVENT = "userRegistered";
export const USER_LOGGED_IN_EVENT = "userLoggedIn";
export const USER_LOGGED_OUT_EVENT = "userLoggedOut";


export const loadDataFromCloud = async (cloudData : string) : Promise<void> => {
  // Prüfe ob V2-Format (ZIP mit meta.json und isV2: true)
  const isV2 = await isV2CloudData(cloudData);
  
  if (isV2) {
    // V2: ZIP mit Dateien + Meta laden
    const editorState = getViewData(EDITOR_VIEW_NAME);
    const projectId = editorState?.project?.id || editorState?.project?.project?.replace(/\s+/g, '_');
    
    console.log('[CloudSync] Loading V2 ZIP from cloud for project:', projectId);
    await loadV2CloudZip(cloudData, projectId);
    
    // CloudData Status setzen
    const cloudStorageData = getCloudData();
    cloudStorageData.loaded = true;
    cloudStorageData.loadError = undefined;
    setCloudData(cloudStorageData);
    return;
  }

  // Legacy: Komprimiertes JSON
  decompressBase64(cloudData, (decompressed) => {
    //console.log('load data from cloud', decompressed);
    const data = JSON.parse(decompressed);
    
    // Log the user code after unpacking    
    console.log('Cloud Data Code Length:', data.code ? data.code.length : 0);
    
    const cloudStorageData = getCloudData();

    cloudStorageData.deleted = data.deleted;
    if (data.deleted) {
      return;
    }

    cloudStorageData.loaded = true;
    cloudStorageData.loadError = undefined; // Clear any previous load errors
    cloudStorageData.code = data.code;
    cloudStorageData.sources = data.sources;
    cloudStorageData.tabs = data.tabs;
    cloudStorageData.assets = data.assets;
    cloudStorageData.replaceKey = data.replaceKey;
    cloudStorageData.initKey = data.initKey;
    cloudStorageData.assetsKey = data.assetsKey;
    cloudStorageData.initCode = data.initCode;
    cloudStorageData.projectAssets = data.projectAssets;
    cloudStorageData.templateCode = data.templateCode;
    
    cloudStorageData.projectName = data.projectName;
    cloudStorageData.projectTask = data.projectTask;
    cloudStorageData.projectHint = data.projectHint;

    cloudStorageData.chatMessages = data.chatMessages;
    cloudStorageData.lastUsedModelId = data.lastUsedModelId;

    //console.log("load data from cloud" , cloudStorageData);
    setCloudData(cloudStorageData);

    const editorState = getViewData(EDITOR_VIEW_NAME);

    editorState.data = {
      ...editorState.data,
      // Use data.code if it exists (even if empty string), otherwise keep existing code
      editorCode : data.code !== undefined ? data.code : editorState.data.editorCode,
      editorTabs : data.sources ? migrateTabUserSources(data.sources, data.tabs) : editorState.data.editorTabs,
      userAssets : data.assets ? data.assets : editorState.data.userAssets,
      assetsList : data.projectAssets ? data.projectAssets : editorState.data.assetsList,
      templateCode : data.templateCode ? data.templateCode : editorState.data.templateCode,
      chatMessages: data.chatMessages ? data.chatMessages : [],
      lastUsedModelId: data.lastUsedModelId,
    }


    editorState.project = {
      ...editorState.project,
      replaceKey : data.replaceKey ? data.replaceKey : editorState.project.replaceKey,
      initKey : data.initKey ? data.initKey : editorState.project.initKey,
      assetsKey : data.assetsKey ? data.assetsKey : editorState.project.assetsKey,
      tabs : data.tabs ? data.tabs : editorState.project.tabs,
      initCode : data.initCode ? data.initCode : editorState.project.initCode,
      project : data.projectName ? data.projectName : editorState.project.project,
      projectOrigin : editorState.project.project,
      task : data.projectTask ? data.projectTask : editorState.project.task,
      hint : data.projectHint ? data.projectHint : editorState.project.hint
    }

    setViewData(EDITOR_VIEW_NAME, editorState);
  });
}

export const loadCloudData = async (key : string, publicState : boolean) : Promise<{success: boolean, data?: any, error?: string, status?: number}>  => {

  let url = `${getCloudServerPath()}/wp-json/data/${escape(key)}`;
  
  // If it's a public project, add status=public query parameter to help backend identify it
  // This is especially important for hashed user identifiers (UID_...) where the backend
  // cannot verify the user identity from the hash
  if (publicState) {
    url += '?status=public';
  }

  const settings = {
    method: "GET",
    headers: {},
    mode : 'cors',
    redirect : 'follow',
    credentials: 'include'
  };

  if (!publicState) {
    settings.headers = {
        'X-WP-Nonce': getCloudUser().nonce
    };
  }

  //console.log("load data for key:" + key);
  const responseData = await doRequest(url, settings);
  console.log(responseData);
  
  // Return result object instead of throwing
  if (!responseData.success || responseData.status === 404) {
    return {
      success: false,
      error: responseData.message || 'Failed to load data',
      status: responseData.status
    };
  }
  
  return {
    success: true,
    data: responseData.data?.data
  };
}

export async function listData() {
  const url = `${getCloudServerPath()}/wp-json/data`;
  const headers = new Headers();
  const nonce = getCloudUser().nonce;
  if (!nonce) throw Error('User not logged in')
  headers.append("X-WP-Nonce", nonce);
  const settings = {
    mode : 'cors',
    redirect : 'follow',
    credentials: 'include',
    method: "GET",
    headers
  }

  const responseData = await doRequest(url, settings);
  //console.log("return data list", responseData);
  return responseData;
}

export function saveDataToCloud() {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  let cloudData = getCloudData();
  cloudData = {
    loaded : false,
    code : editorState.data?.editorCode,
    sources : editorState.data?.editorTabs,
    assets : editorState.data?.userAssets,
    replaceKey : editorState.project?.replaceKey,
    initKey : editorState.project?.initKey,
    assetsKey : editorState.project?.assetsKey,
    tabs : editorState.project?.tabs,
    initCode : editorState.project?.initCode,
    projectAssets : editorState.data?.assetsList,
    templateCode : editorState.data?.templateCode,
    deleted : false,
    cloudStateLoaded : cloudData?.cloudStateLoaded,
    projectName : editorState.project?.project,
    projectHint : editorState.project?.hint,
    projectTask : editorState.project?.task,
    chatMessages: editorState.data?.chatMessages || [],
    lastUsedModelId: editorState.data?.lastUsedModelId,
  }
  setCloudData(cloudData)
  return JSON.stringify(cloudData);
}

export function removeDataFromCloud() {
  let cloudData = getCloudData();
  cloudData = {
    loaded : false,
    code : "",
    sources : "",
    assets : "",
    replaceKey : "",
    initKey : "",
    assetsKey : "",
    tabs : [],
    initCode : "",
    projectAssets : "",
    templateCode : "",
    deleted : true,
    cloudStateLoaded : false
  }
  setCloudData(cloudData)
  return JSON.stringify(cloudData);
}

export async function saveData(key : string, value : any, hash? : string) {

  const form = new FormData();
  let url = `${getCloudServerPath()}/wp-json/data/save?dataKey=${escape(key)}`;

  const jsonValue = JSON.stringify({ data: value });

  if (hash === undefined)  {
    form.append("data", jsonValue);
  } else {
    // wasted too much time for this crypto shit...
    // FUCK IT CryptoWhateverJS. Next time we will try Web Crypt https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/encrypt
    // note for future self: implement as AES-GCM...
    // form.append("data", CryptoES.AES.encrypt(value, hash));
    url += "&hash=" + escape(hash);
  }

  const nonce = getCloudUser().nonce;

  const headers = new Headers();
  headers.append("Accept", "application/json");
  if (nonce) {
    headers.append("X-WP-Nonce", nonce);
  }

  const settings = {
    method: "POST",
    headers,
    mode : 'cors',
    redirect : 'follow',
    credentials: 'include',
    body: form
  }

  const responseData = await doRequest(url, settings);
  if (!responseData.invalid || !responseData.success) {
    console.log("saved Data ", key);

  }
  //if saving not worked it throws an error
}

export async function changeStatus(key : string, status : string) {

  const url = `${getCloudServerPath()}/wp-json/data/${escape(key)}?status=${escape(status)}`;
  const headers = new Headers();
  headers.append("Accept", "application/json");
  const nonce = getCloudUser().nonce;
  if (nonce) {
    headers.append("X-WP-Nonce", nonce);
  }

  const settings = {
    method: "POST",
    headers,
    mode : 'cors',
    redirect : 'follow',
    credentials: 'include'
  }

  const responseData = await doRequest(url, settings);
  if (!responseData.invalid || !responseData.success) {
    console.log("saving worked", responseData);
  }
}

export async function deleteDataFromCloud(deleteKey : string) {
  const url = `${getCloudServerPath()}/wp-json/data/delete`;
  const form = new FormData();
  form.append("key", "delete");
  form.append("deleteKey", deleteKey);
  
  const headers = new Headers();
  headers.append("Accept", "application/json");
  const nonce = getCloudUser().nonce;
  if (nonce) {
    headers.append("X-WP-Nonce", nonce);
  }

  const settings = {
    method: "POST",
    headers,
    mode : 'cors',
    redirect : 'follow',
    credentials: 'include',
    body: form
  }

  const responseData = await doRequest(url, settings);
  if (!responseData.invalid || !responseData.success) {
    console.log("delete worked", responseData);
  }
  return responseData;
}